import { useZero } from "@rocicorp/zero/react";
import { useCallback, useMemo } from "react";
import { sharedMutators } from "../../../../shared/mutators";
import { queries } from "../../../../shared/queries";
import {
  BUILTIN_ROLES,
  type GrantEntry,
  type RoleOption,
} from "../../../../shared/rbac/roles";
import { useCurrentUserId } from "../../../hooks/useCurrentUserId";
import { useManageAccessGuard } from "../../../hooks/useManageAccessGuard";
import { usePrincipalModals } from "../../../hooks/usePrincipalModals";
import { useQuery } from "../../../hooks/useQuery";
import { emitErrorToast } from "../../../lib/errorToast";
import { Button } from "../../Button";
import { Modal } from "../../Modal";
import { useScopeAccessCloseWarning } from "../../ScopeAccessCloseWarning";
import { ScopeAccessTable } from "../../ScopeAccessTable";
import {
  HeaderRight,
  SettingsModalBody,
  SettingsModalFooter,
  SettingsModalHeader,
} from "../../SettingsLayout";
import { Text } from "../../Text";
import {
  Card,
  ColoredIconBadge,
  SectionHeading,
  settingsModalStyle,
} from "../shared";

const ADMIN_ROLE: RoleOption = {
  builtinRoleId: "tenant_admin",
  label: BUILTIN_ROLES.tenant_admin.label,
};

const ROLE_OPTIONS: RoleOption[] = [ADMIN_ROLE];

const isManagerGrant = () => true;

type Props = {
  onClose: () => void;
};

export function TenantAdminSettings({ onClose }: Props) {
  const z = useZero();
  const currentUserId = useCurrentUserId();
  const [allUsers] = useQuery(queries.allUsers());
  const [adminGrants] = useQuery(queries.tenantAdminGrants());
  const { onOpenUser } = usePrincipalModals();
  const { stagedGrantsRef, requestClose, closeWarningModal } =
    useScopeAccessCloseWarning({ singular: "admin", plural: "admins" });

  const activeUsers = useMemo(
    () => (allUsers ?? []).filter((u) => !u.deactivated),
    [allUsers]
  );

  const adminUserIds = useMemo(
    () =>
      new Set(
        adminGrants.flatMap((g) =>
          g.user && !g.user.deactivated ? [g.user.id] : []
        )
      ),
    [adminGrants]
  );

  const allGrants: GrantEntry[] = useMemo(
    () =>
      [...adminUserIds].map((userId) => ({
        userId,
        builtinRoleId: "tenant_admin",
      })),
    [adminUserIds]
  );

  const assignedUsers = useMemo(
    () => activeUsers.filter((u) => adminUserIds.has(u.id)),
    [activeUsers, adminUserIds]
  );

  const doCommit = useCallback(
    (grants: GrantEntry[]) => {
      const userIds = [
        ...new Set(grants.flatMap((g) => (g.userId ? [g.userId] : []))),
      ];
      z.mutate(sharedMutators.setTenantAdmins({ userIds }));
    },
    [z]
  );

  const viewerRetainsManage = useCallback(
    (grants: GrantEntry[]) => grants.some((g) => g.userId === currentUserId),
    [currentUserId]
  );

  const { commitGrants, confirmModal } = useManageAccessGuard({
    allGrants,
    doCommit,
    isManagerGrant,
    viewerRetainsManage,
    resourceLabel: "tenant",
  });

  // The mutator's schema rejects an empty set outright, so the last admin is a
  // hard block rather than something the manage-access guard can confirm past.
  const removeUser = useCallback(
    (userId: string) => {
      const remaining = allGrants.filter((g) => g.userId !== userId);
      if (remaining.length === 0) {
        emitErrorToast({
          icon: "circle-off",
          message: "There must be at least one tenant admin.",
          buttonLabel: "Dismiss",
        });
        return;
      }
      commitGrants(remaining);
    },
    [allGrants, commitGrants]
  );

  const handleClose = useCallback(
    () => requestClose(onClose),
    [onClose, requestClose]
  );

  return (
    <>
      <Modal open onClose={handleClose} style={settingsModalStyle}>
        <SettingsModalHeader>
          <ColoredIconBadge id="__tenant-admins__" iconName="building-2" />
          <Text variant="text-regular-md">Tenant admins</Text>
          <HeaderRight>
            <Button
              size="l"
              variant="secondary"
              icon="x"
              onClick={handleClose}
            />
          </HeaderRight>
        </SettingsModalHeader>

        <SettingsModalBody style={{ gap: 24 }} data-popover-host>
          <Card>
            <SectionHeading>
              <Text variant="text-regular-md">Members</Text>
              <Text variant="text-small" color="var(--text-secondary)">
                Tenant admins have full access to everything.
              </Text>
            </SectionHeading>
            <ScopeAccessTable
              allGrants={allGrants}
              assignedGroups={[]}
              assignedUsers={assignedUsers}
              roleOptions={ROLE_OPTIONS}
              allGroups={[]}
              allUsers={activeUsers}
              customRoles={[]}
              canManage
              compact
              commitGrants={commitGrants}
              removeGroup={() => {}}
              removeUser={removeUser}
              memberRoster
              onOpenUser={onOpenUser}
              stagedGrantsRef={stagedGrantsRef}
              searchPlaceholder="Add members…"
              defaultRole={ADMIN_ROLE}
            />
          </Card>
        </SettingsModalBody>

        <SettingsModalFooter style={{ gap: 8 }}>
          <Button size="l" onClick={handleClose}>
            Done
          </Button>
        </SettingsModalFooter>
      </Modal>
      {confirmModal}
      {closeWarningModal}
    </>
  );
}
